import React, { useState } from 'react';
import axios from 'axios';
import { rootUrl } from '../../../../config/config';
import { 
    CheckCircleIcon, 
    LockClosedIcon, 
    LockOpenIcon,
    DocumentDuplicateIcon,
    CreditCardIcon,
    PlusCircleIcon
} from '@heroicons/react/24/solid';

const RemittanceStatusTimeline = ({ cars, token }) => {
    const [selectedCarTimeline, setSelectedCarTimeline] = useState(null);
    const [timelineData, setTimelineData] = useState({});
    const [loading, setLoading] = useState({});

    const loadTimeline = async (remittanceId, plateNo) => {
        if (timelineData[remittanceId]) {
            // Already loaded
            setSelectedCarTimeline(plateNo);
            return;
        }

        setLoading(prev => ({ ...prev, [remittanceId]: true }));

        try {
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(
                `${rootUrl}/api/remittance/${remittanceId}/timeline`,
                { headers }
            );

            if (response.data.success) {
                setTimelineData(prev => ({
                    ...prev,
                    [remittanceId]: response.data.data.timeline
                }));
                setSelectedCarTimeline(plateNo);
            }
        } catch (error) {
            console.error('Error loading timeline:', error);
        } finally {
            setLoading(prev => ({ ...prev, [remittanceId]: false }));
        }
    };

    const getEventIcon = (eventType) => {
        const icons = {
            CREATED: <PlusCircleIcon className="w-6 h-6" />,
            PROCESSED: <CheckCircleIcon className="w-6 h-6" />,
            LOCKED: <LockClosedIcon className="w-6 h-6" />,
            UNLOCKED: <LockOpenIcon className="w-6 h-6" />,
            APPROVED: <CheckCircleIcon className="w-6 h-6" />,
            PAID: <CreditCardIcon className="w-6 h-6" />,
            VERSION_SAVED: <DocumentDuplicateIcon className="w-6 h-6" />,
            PAYMENT_ALLOCATED: <CreditCardIcon className="w-6 h-6" />
        };
        return icons[eventType] || <CheckCircleIcon className="w-6 h-6" />;
    };

    const getEventColor = (eventType) => {
        const colors = {
            CREATED: 'bg-gray-100 text-gray-700',
            PROCESSED: 'bg-blue-100 text-blue-700',
            LOCKED: 'bg-yellow-100 text-yellow-700',
            UNLOCKED: 'bg-orange-100 text-orange-700',
            APPROVED: 'bg-green-100 text-green-700',
            PAID: 'bg-purple-100 text-purple-700',
            VERSION_SAVED: 'bg-indigo-100 text-indigo-700',
            PAYMENT_ALLOCATED: 'bg-teal-100 text-teal-700'
        };
        return colors[eventType] || 'bg-gray-100 text-gray-700';
    };

    if (!cars || cars.length === 0) {
        return <div className="text-center text-gray-500">No vehicles found</div>;
    }

    return (
        <div className="space-y-6">
            {/* Vehicle Selector */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-[#2a2e30] mb-4">Select Vehicle to View Timeline</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {cars.map((car) => (
                        <button
                            key={car.plate_no}
                            onClick={() => loadTimeline(car.id, car.plate_no)}
                            disabled={loading[car.id]}
                            className={`p-4 rounded-lg border-2 transition-all ${
                                selectedCarTimeline === car.plate_no
                                    ? 'border-[#FF5733] bg-[#fff8f5]'
                                    : 'border-gray-200 bg-white hover:border-[#FF5733] hover:bg-gray-50'
                            } ${loading[car.id] ? 'opacity-50 cursor-wait' : ''}`}
                        >
                            <div className="font-bold text-[#2a2e30]">{car.plate_no}</div>
                            <div className="text-sm text-gray-600">
                                {car.make} {car.model}
                            </div>
                            <div className={`mt-2 inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                                car.status === 'PAID' 
                                    ? 'bg-purple-100 text-purple-800'
                                    : car.status === 'APPROVED'
                                    ? 'bg-green-100 text-green-800'
                                    : car.status === 'PROCESSED'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-yellow-100 text-yellow-800'
                            }`}>
                                {car.status}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Timeline Display */}
            {selectedCarTimeline && (() => {
                const car = cars.find(c => c.plate_no === selectedCarTimeline);
                const timeline = timelineData[car.id];

                if (!timeline) {
                    return (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#FF5733] mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading timeline...</p>
                        </div>
                    );
                }

                return (
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-[#2a2e30]">
                                Timeline for {selectedCarTimeline}
                            </h3>
                            <div className="text-sm text-gray-600">
                                {timeline.length} event{timeline.length !== 1 ? 's' : ''}
                            </div>
                        </div>

                        {/* Timeline */}
                        <div className="relative">
                            {/* Vertical line */}
                            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                            {/* Events */}
                            <div className="space-y-6">
                                {timeline.map((event, index) => (
                                    <div key={index} className="relative flex gap-4">
                                        {/* Icon */}
                                        <div className={`flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center z-10 ${getEventColor(event.event_type)}`}>
                                            {getEventIcon(event.event_type)}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 pt-2">
                                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                <div className="flex items-start justify-between mb-2">
                                                    <div>
                                                        <div className="font-bold text-[#2a2e30]">
                                                            {event.event_type.replace(/_/g, ' ')}
                                                        </div>
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            {event.description}
                                                        </div>
                                                    </div>
                                                    <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                                        event.status === 'PAID' 
                                                            ? 'bg-purple-100 text-purple-800'
                                                            : event.status === 'APPROVED'
                                                            ? 'bg-green-100 text-green-800'
                                                            : event.status === 'PROCESSED'
                                                            ? 'bg-blue-100 text-blue-800'
                                                            : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                        {event.status}
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-4 text-sm text-gray-600 mt-3">
                                                    <div className="flex items-center gap-1">
                                                        <span className="font-semibold">By:</span>
                                                        <span>{event.user}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <span className="font-semibold">Date:</span>
                                                        <span>
                                                            {new Date(event.timestamp).toLocaleString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric',
                                                                hour: 'numeric',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    </div>
                                                </div>

                                                {event.reference && (
                                                    <div className="mt-2 text-xs text-gray-500 font-mono bg-white px-2 py-1 rounded border border-gray-200">
                                                        Ref: {event.reference}
                                                    </div>
                                                )}

                                                {event.amount && (
                                                    <div className="mt-2 text-sm font-semibold text-green-700">
                                                        Amount: {new Intl.NumberFormat('en-KE', {
                                                            style: 'currency',
                                                            currency: 'KES',
                                                            minimumFractionDigits: 2
                                                        }).format(event.amount)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default RemittanceStatusTimeline;
